import {
  useState, useContext, useEffect, useRef,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../context/AuthContext';
import { isAdminAccount } from '../utils/permissions';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import { SITE_NAME } from '../config/site';

/**
 * Header for signed-in players. Includes the account dropdown.
 */
function Header() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Don't render if no user (use UnauthenticatedHeader instead)
  if (!user) {
    return null;
  }

  return (
    <header className="fixed left-0 right-0 top-0 h-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4 z-40">
      <Link to="/" className="flex items-center gap-2">
        <Logo className="w-8 h-8" />
        <span className="hidden sm:inline text-xl font-semibold text-gray-900 dark:text-white">
          {SITE_NAME}
        </span>
      </Link>

      <nav className="hidden md:flex items-center gap-6">
        <Link to="/play" className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary transition-colors">Play</Link>
        <Link to="/leaderboard" className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary transition-colors">Leaderboard</Link>
        {isAdminAccount(user) && (
          <Link to="/admin" className="text-sm font-medium text-primary hover:text-primary-dark transition-colors">Admin</Link>
        )}
      </nav>

      <div className="flex items-center gap-4">
        <ThemeToggle />
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-3"
          >
            {user.profilePicture ? (
              <img
                key={user.profilePicture}
                src={user.profilePicture}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-navy text-sm font-medium">
                {(user.username || user.email)?.[0].toUpperCase()}
              </div>
            )}
            <span className="hidden sm:inline text-sm font-medium text-gray-900 dark:text-white max-w-[160px] truncate">
              {user.username || user.email}
            </span>
            <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {isUserMenuOpen && (
            <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
              <Link
                to="/play"
                onClick={() => setIsUserMenuOpen(false)}
                className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Play
              </Link>
              <Link
                to="/leaderboard"
                onClick={() => setIsUserMenuOpen(false)}
                className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Leaderboard
              </Link>
              <Link
                to="/profile"
                onClick={() => setIsUserMenuOpen(false)}
                className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Profile
              </Link>
              {isAdminAccount(user) && (
                <Link
                  to="/admin"
                  onClick={() => setIsUserMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-primary font-semibold hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Admin
                </Link>
              )}
              <hr className="my-1 border-gray-200 dark:border-gray-700" />
              <button
                type="button"
                onClick={() => {
                  logout();
                  setIsUserMenuOpen(false);
                  navigate('/');
                }}
                className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 w-full"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default Header;
